/**
 * Customers — the tenant's directory of everyone who books, registers or joins
 * a queue. One record per person; the columns are the tenant's own, saved for
 * everyone in the organisation and edited through the Columns panel.
 *
 * Everything that narrows the list happens ON THE SERVER: `q`, `customerTypeId`
 * / `untyped`, `sort` + `direction`, `page` + `limit`. Nothing is filtered or
 * sorted across the loaded page — a page is a window on the directory, not the
 * directory. `pagination.total` is the filtered count and the page boundary;
 * `summary.*` are the filter-INDEPENDENT directory totals behind the KPI cards,
 * which is why the two numbers legitimately differ while a filter is on.
 *
 * Read-only. There is no create/edit form, export, message, book or register
 * action on this surface; the one supported write is the customer-type
 * assignment on the detail, and the shared column layout through Columns.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Btn, Dropdown, Icon, Input, PageState, Pagination, SegmentedControl, T, Table, type TableColumn } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { useNav } from "../../shell/router";
import { can } from "../../shell/access";
import { useIsMobile } from "../../shell/useResponsive";
import { useT } from "../../lib/i18n";
import { useCustomerTypeCatalog } from "./customer-types";
import { TypeCatalogNotice } from "./CustomerTypeCard";
import {
  CUSTOMER_OPS, DIRECTORY_LIMIT_DEFAULT, getCustomersSettings, listBookingCustomers, useScopedRead,
  type CustomerDirectoryRecord, type CustomerListSortKey, type DirectoryPage,
} from "./customers-data";
import {
  CUSTOMER_LIST_SORT_KEYS, EM_DASH, PLATFORM_DEFAULT_SORT, isSortableColumn, normalizeColumns,
} from "./customer-columns";
import { CustomerCell, Kpi, ReadError, useColumnLabel, useCustomerNoun, useIcu, initialsOf } from "./customers-shared";
import ColumnsPanel from "./ColumnsPanel";

const UNTYPED = "__untyped";
const ALL = "__all";
const SEARCH_DEBOUNCE_MS = 300;

export default function CustomersList() {
  const t = useT();
  const icu = useIcu();
  const nav = useNav();
  const { noun, nounPlural } = useCustomerNoun();
  const phone = useIsMobile(640);
  const columnLabel = useColumnLabel();
  const catalog = useCustomerTypeCatalog(can("customer_types.read"));

  /* ── tenant list configuration ─────────────────────────────────────── */
  const settingsRead = useScopedRead("customers-settings", getCustomersSettings);
  const settings = settingsRead.data?.data ?? null;
  const columns = useMemo(() => (settings ? normalizeColumns(settings.columns) : []), [settings]);

  /* ── query state ───────────────────────────────────────────────────── */
  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>(ALL);
  const [page, setPage] = useState(1);
  const [sortOverride, setSortOverride] = useState<{ key: CustomerListSortKey; direction: "asc" | "desc" } | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const columnsButtonRef = useRef<HTMLButtonElement | null>(null);

  // Debounced, server-side search. Every change resets to page 1 — a filtered
  // result set has different page boundaries, so keeping the page number would
  // land the operator on an empty page of a non-empty result.
  useEffect(() => {
    const id = window.setTimeout(() => { setQ(typed.trim()); setPage(1); }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [typed]);

  const sort = sortOverride ?? settings?.defaultSort ?? PLATFORM_DEFAULT_SORT;
  const externalIdSystem = settings?.externalIdSystem ?? null;

  const queryKey = [
    "customers", q, typeFilter, page, sort.key, sort.direction, externalIdSystem ?? "-",
    settings ? "ready" : "pending",
  ].join("|");

  const directory = useScopedRead<DirectoryPage>(
    queryKey,
    () => listBookingCustomers({
      q: q || undefined,
      customerTypeId: typeFilter !== ALL && typeFilter !== UNTYPED ? typeFilter : undefined,
      untyped: typeFilter === UNTYPED,
      externalIdSystem,
      sort: sort.key,
      direction: sort.direction,
      page,
      limit: DIRECTORY_LIMIT_DEFAULT,
    }),
    !!settings,
  );

  const rows = directory.data?.data ?? [];
  const pagination = directory.data?.pagination ?? null;
  const summary = directory.data?.summary ?? null;
  const total = pagination?.total ?? 0;
  const totalPages = pagination && pagination.limit > 0 ? Math.max(1, Math.ceil(pagination.total / pagination.limit)) : 1;
  const filtersActive = !!q || typeFilter !== ALL;

  const openCustomer = useCallback((id: string) => nav.go("customers.detail", { customerId: id }), [nav]);

  const changeType = (next: string) => { setTypeFilter(next); setPage(1); };
  const changeSort = (key: CustomerListSortKey) => {
    setSortOverride((prev) => {
      const base = prev ?? sort;
      return base.key === key
        ? { key, direction: base.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" };
    });
    setPage(1);
  };

  /* ── type filter chips ─────────────────────────────────────────────── */
  const typeOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string; count?: number }> = [
      { value: ALL, label: t("customers.filters.all", "All"), count: summary?.customers },
    ];
    for (const ct of summary?.customerTypes ?? []) opts.push({ value: ct.id, label: ct.name, count: ct.count });
    // "Untyped" is offered only when it would match something. A selected
    // Untyped filter keeps its chip so the active filter is never invisible.
    if ((summary?.untyped ?? 0) > 0 || typeFilter === UNTYPED) {
      opts.push({ value: UNTYPED, label: t("customers.filters.untyped", "Untyped"), count: summary?.untyped });
    }
    return opts;
  }, [summary, typeFilter, t]);

  // A type that vanished from the directory summary (archived, rescoped) must
  // not leave the list filtered by an invisible reason.
  useEffect(() => {
    if (typeFilter === ALL || typeFilter === UNTYPED || !summary) return;
    if (!summary.customerTypes.some((ct) => ct.id === typeFilter)) { setTypeFilter(ALL); setPage(1); }
  }, [summary, typeFilter]);

  /* ── table columns ─────────────────────────────────────────────────── */
  const externalIdLabel = externalIdSystem ? t(`customers.externalIdSystem.${externalIdSystem}`, externalIdSystem.toUpperCase()) : null;

  const tableColumns: Array<TableColumn<CustomerDirectoryRecord>> = columns.map((id) => {
    const label = columnLabel(id);
    const sortable = isSortableColumn(id);
    const align: "left" | "right" = ["appointments", "eventRegistrations", "eventCheckIns", "queueJoins"].includes(id) ? "right" : "left";
    const active = sort.key === id;
    return {
      id,
      align,
      minWidth: id === "name" ? 220 : undefined,
      header: sortable ? (
        <button
          type="button"
          onClick={() => changeSort(id as CustomerListSortKey)}
          aria-label={icu("customers.list.sortAriaLabel", "Sort by {column}", { column: label })}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5, border: 0, background: "transparent",
            padding: 0, font: "inherit", color: "inherit", cursor: "pointer", letterSpacing: "inherit", textTransform: "inherit",
          }}
        >
          <span>{label}</span>
          <span aria-hidden="true" style={{ color: active ? T.n1 : T.n4, fontSize: 10 }}>
            {active ? (sort.direction === "asc" ? "\u25B2" : "\u25BC") : "\u21C5"}
          </span>
        </button>
      ) : label,
      cell: (row) => <CustomerCell row={row} columnId={id} catalog={catalog} externalIdLabel={externalIdLabel} />,
    };
  });

  const sortAnnouncement = (() => {
    const label = columnLabel(sort.key);
    return sort.direction === "asc"
      ? icu("customers.list.sortedAscending", "Sorted by {column}, ascending", { column: label })
      : icu("customers.list.sortedDescending", "Sorted by {column}, descending", { column: label });
  })();

  /* ── settings failure is not an empty directory ────────────────────── */
  const settingsFailure = settingsRead.failure;

  return (
    <Screen rail="customers">
      <PageHeader
        title={nounPlural}
        subtitle={icu(
          "customers.list.subtitle",
          "Everyone who books, registers or joins a queue \u2014 one record per {noun}, with their full history.",
          { noun: noun.toLowerCase() },
        )}
      />
      <DataSourceStrip screen="customers" ops={CUSTOMER_OPS} reqId="REQ-069" />

      <div style={{ padding: "14px 24px 48px", display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
        {/* Filter-independent directory totals for the authorised scope. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
          <Kpi label={nounPlural} value={summary ? summary.customers : EM_DASH} />
          <Kpi label={t("customers.kpi.appointments", "Appointments")} value={summary ? summary.appointments : EM_DASH} />
          <Kpi label={t("customers.kpi.eventCheckIns", "Event check-ins")} value={summary ? summary.eventCheckIns : EM_DASH} />
          <Kpi label={t("customers.kpi.queueJoins", "Queue joins")} value={summary ? summary.queueJoins : EM_DASH} />
        </div>

        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <SegmentedControl<string>
            size="sm"
            ariaLabel={icu("customers.filters.ariaLabel", "Filter {nounPlural} by type", { nounPlural })}
            value={typeFilter}
            onChange={changeType}
            options={typeOptions}
          />
          <div style={{ flex: 1, minWidth: 220 }}>
            <Input
              size="sm"
              type="search"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              iconLeft={<Icon.search size={13} />}
              placeholder={icu("customers.list.searchPlaceholder", "Search {nounPlural} by name, email or ID", { nounPlural: nounPlural.toLowerCase() })}
              aria-label={icu("customers.list.searchAriaLabel", "Search {nounPlural}", { nounPlural: nounPlural.toLowerCase() })}
            />
          </div>
          {phone ? (
            <div style={{ minWidth: 180 }}>
              <Dropdown<string>
                size="sm"
                portal
                aria-label={t("customers.list.sortControl", "Sort")}
                value={sort.key}
                onChange={(k) => changeSort(k as CustomerListSortKey)}
                options={CUSTOMER_LIST_SORT_KEYS.filter((k) => columns.includes(k)).map((k) => ({ value: k, label: columnLabel(k) }))}
              />
            </div>
          ) : null}
          {can("settings.write") ? (
            <Btn
              ref={columnsButtonRef}
              variant="secondary"
              size="sm"
              icon={<Icon.grid size={13} />}
              onClick={() => setPanelOpen(true)}
            >{t("customers.columnsPanel.open", "Columns")}</Btn>
          ) : null}
        </div>

        <TypeCatalogNotice catalog={catalog} />

        {settingsFailure ? (
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8 }}>
            <ReadError
              message={settingsFailure.denied
                ? t("customers.list.settingsDenied", "Your role can\u2019t read this organisation\u2019s list configuration yet, so the columns below can\u2019t be shown.")
                : `${t("customers.list.settingsFailed", "The column configuration couldn\u2019t be read.")} ${settingsFailure.message}`}
              onRetry={settingsFailure.denied ? undefined : settingsRead.reload}
              retryLabel={t("common.actions.retry", "Retry")}
            />
          </div>
        ) : (
          <PageState
            noun={nounPlural.toLowerCase()}
            loading={settingsRead.loading || directory.loading}
            error={directory.failure ? directory.failure.message : null}
            empty={!directory.loading && !directory.failure && rows.length === 0}
            filtered={filtersActive}
            skeleton="table"
            skeletonColumns={Math.min(columns.length || 4, 6)}
            onRetry={directory.reload}
            onClearFilters={filtersActive ? () => { setTyped(""); setQ(""); setTypeFilter(ALL); setPage(1); } : undefined}
            errorTitle={t("customers.list.readFailed.title", "This list couldn\u2019t be loaded")}
            errorBody={t("customers.list.readFailed.body", "The directory read failed. Nothing has been lost \u2014 try again.")}
            retryLabel={t("common.actions.retry", "Retry")}
            emptyTitle={t("customers.list.empty.title", "Nothing here yet")}
            emptyBody={t("customers.list.empty.body", "Records appear here as soon as someone books, registers or joins a queue.")}
            filteredTitle={t("customers.list.emptyFiltered.title", "No matches")}
            filteredBody={t("customers.list.emptyFiltered.body", "Clear the search or choose a different type.")}
            clearFiltersLabel={t("common.actions.clearFilters", "Clear filters")}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <p aria-live="polite" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", margin: 0 }}>
                {sortAnnouncement}
              </p>

              {phone ? (
                <MobileRows rows={rows} columns={columns} catalog={catalog} externalIdLabel={externalIdLabel} columnLabel={columnLabel} onOpen={openCustomer} />
              ) : (
                <Table<CustomerDirectoryRecord>
                  testid="customers-table"
                  caption={icu("customers.list.tableAriaLabel", "{nounPlural}", { nounPlural })}
                  columns={tableColumns}
                  rows={rows}
                  getRowId={(r) => r.id}
                  sort={{ columnId: sort.key, direction: sort.direction }}
                  onRowClick={(r) => openCustomer(r.id)}
                  emptyState={t("customers.list.emptyFiltered.title", "No matches")}
                />
              )}

              <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                <span style={{ fontSize: 12.5, color: T.soft }}>
                  {icu("customers.list.showingCount", "Showing {shown} of {total}", { shown: rows.length, total })}
                </span>
                {totalPages > 1 ? (
                  <Pagination
                    size="sm"
                    showStatus={false}
                    aria-label={icu("customers.list.paginationAriaLabel", "{nounPlural} pages", { nounPlural })}
                    totalPages={totalPages}
                    currentPage={pagination?.page ?? page}
                    onPageChange={setPage}
                  />
                ) : null}
              </div>
            </div>
          </PageState>
        )}
      </div>

      {panelOpen && settings ? (
        <ColumnsPanel
          settings={settings}
          onClose={() => { setPanelOpen(false); columnsButtonRef.current?.focus(); }}
          onSaved={() => { settingsRead.reload(); }}
        />
      ) : null}
    </Screen>
  );
}

/**
 * Below 640px the table becomes one card per record: Name first, then the
 * configured fields BENEATH it in the saved order — the same layout decision,
 * re-flowed. No horizontal page overflow at 320px.
 */
function MobileRows({ rows, columns, catalog, externalIdLabel, columnLabel, onOpen }: {
  rows: CustomerDirectoryRecord[];
  columns: string[];
  catalog: ReturnType<typeof useCustomerTypeCatalog>;
  externalIdLabel: string | null;
  columnLabel: (id: string) => string;
  onOpen: (id: string) => void;
}) {
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
      {rows.map((row) => (
        <li key={row.id}>
          <button
            type="button"
            onClick={() => onOpen(row.id)}
            style={{
              width: "100%", textAlign: "left", background: T.white, border: `1px solid ${T.border}`,
              borderRadius: 8, boxShadow: T.shadowSm, padding: 14, cursor: "pointer", fontFamily: T.font,
              display: "flex", flexDirection: "column", gap: 10, minWidth: 0,
            }}
          >
            <span style={{ display: "flex", gap: 10, alignItems: "center", minWidth: 0 }}>
              <span style={{
                width: 32, height: 32, borderRadius: 999, background: T.p1, color: T.onColor,
                display: "grid", placeItems: "center", fontSize: 12, fontWeight: 600, flexShrink: 0,
              }}>{initialsOf(row.displayName)}</span>
              <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: T.n1, overflow: "hidden", textOverflow: "ellipsis" }}>{row.displayName}</span>
                {row.externalId?.value ? (
                  <span style={{ fontFamily: T.fontMono, fontSize: 11, color: T.soft }}>
                    {externalIdLabel ? `${externalIdLabel} ${row.externalId.value}` : row.externalId.value}
                  </span>
                ) : null}
              </span>
            </span>
            <span style={{ display: "grid", gridTemplateColumns: "minmax(96px, 40%) 1fr", gap: "6px 12px", minWidth: 0 }}>
              {columns.filter((id) => id !== "name").map((id) => (
                <span key={id} style={{ display: "contents" }}>
                  <span style={{ fontSize: 11.5, color: T.soft, fontWeight: 600 }}>{columnLabel(id)}</span>
                  <span style={{ fontSize: 12.5, color: T.n1, minWidth: 0, overflowWrap: "anywhere" }}>
                    <CustomerCell row={row} columnId={id} catalog={catalog} externalIdLabel={externalIdLabel} />
                  </span>
                </span>
              ))}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
